import clsx from 'clsx';
import { ProductPrice } from './ProductPrice';
import { usePDPData, usePDPState } from '~/features/product-details/context';
import {
    PriceLike,
    SalesforceProduct,
    SalesforceProductPriceType,
    SalesforceProductPromotionType,
} from '~/lib/salesforce/types';
import { Typography } from '~/shared/components';
import styles from './ProductPrices.module.scss';
import { getProductSchemaPrice } from '~/services/schemas/product/salesforce';
import { useProductDisplayPrices } from '~/features/product-details/components/ProductOverview/hooks/useProductDisplayPrices';
import { useMemo } from 'react';
import { useTranslation } from '~/shared/utils/translation';
import { toOre } from '~/shared/utils/numbers';

type Props = React.HTMLAttributes<HTMLUListElement>;

/**
 * Salesforce prices a product with a promotion still counting down, but the basket only applies
 * it at zero, so until then the regular price is the one the customer pays.
 */
const getCountdownRegularPrice = (
    product: SalesforceProduct,
    fallbackLabel: string,
    price?: number | null,
): PriceLike | undefined => {
    if (price == null) return undefined;

    // Reuses the label Salesforce gives the regular price, e.g. "Pris".
    const listedPrice = product.c_prices?.find(({ value }) => toOre(value) === toOre(price));

    return {
        type: SalesforceProductPriceType.DEFAULT,
        value: price,
        label: listedPrice?.label || fallbackLabel,
    };
};

export const ProductPrices: React.FC<Props> = ({ className, ...rest }) => {
    const { translate } = useTranslation();
    const { product, currency } = usePDPData();
    const { promotionCountdown, isPromotionDiscountHeldBack } = usePDPState();
    const displayPrices = useProductDisplayPrices(product);
    const countdownRegularPrice = isPromotionDiscountHeldBack
        ? getCountdownRegularPrice(
              product,
              translate('pdp.countdown.promotion.regularPriceLabel') || 'Pris',
              promotionCountdown?.price,
          )
        : undefined;
    const prices = countdownRegularPrice ? [countdownRegularPrice] : displayPrices;
    const schemaPrice = countdownRegularPrice ?? getProductSchemaPrice(product.c_prices);

    const christmasPriceTooltip = useMemo(
        () => (
            <>
                <Typography className={styles.priceTooltipHeading} variant="paragraph-L" as="p">
                    {translate('pdp.tooltips.christmasPriceTitle')}
                </Typography>

                <Typography variant="paragraph-M" as="p">
                    {translate('pdp.tooltips.christmasPriceText')}
                </Typography>
            </>
        ),
        // No dependencies on purpose: the client translation dictionary is a module-level
        // singleton built once per page load, so these two strings cannot change afterwards.
        [],
    );

    if (!prices.length) return null;

    return (
        <>
            <meta itemProp="priceCurrency" content={currency} />
            {schemaPrice && <meta itemProp="price" content={String(schemaPrice.value)} />}

            <ul className={clsx(styles.productPriceList, className)} {...rest}>
                {prices.map((price, index) => {
                    const isChristmasSalePrice =
                        price.promotype === SalesforceProductPromotionType.CHRISTMAS_PRICE &&
                        price.type === SalesforceProductPriceType.SALE;

                    return (
                        <ProductPrice
                            key={`${price.type}-${index}`}
                            price={price}
                            as="li"
                            tooltip={isChristmasSalePrice ? christmasPriceTooltip : null}
                        />
                    );
                })}
            </ul>
        </>
    );
};
